import { type ApolloClient, useApolloClient } from '@apollo/client';
import { isNil, isObject } from 'lodash/fp';
import { useCallback } from 'react';
import {
    DeleteMobilityModuleEmailAssetDocument,
    type DeleteMobilityModuleEmailAssetMutation,
    type DeleteMobilityModuleEmailAssetMutationVariables,
} from '../../../../api/mutations/deleteMobilityModuleEmailAsset';
import {
    UploadMobilityModuleEmailAssetDocument,
    type UploadMobilityModuleEmailAssetMutation,
    type UploadMobilityModuleEmailAssetMutationVariables,
} from '../../../../api/mutations/uploadMobilityModuleEmailAsset';
import {
    type EmailContentUpdateType,
    MobilityAsset,
    MobilityEmailAssetType,
    MobilityRecipient,
} from '../../../../api/types';
import {
    type MobilityCustomerEmailContentInput,
    type MobilityEmailContentFormikValue,
    type MobilityEmailContentInputForm,
    type MobilityOperatorEmailContentInput,
} from './shared';

const useHandleMobilityEmailAsset = () => {
    const apolloClient = useApolloClient();

    return useCallback(
        async (
            moduleId: string,
            values: MobilityEmailContentFormikValue,
            emailContentUpdateType: EmailContentUpdateType,
            initialValues?: MobilityEmailContentFormikValue,
            dealerId?: string
        ) => {
            const promises = values.emailContents.flatMap(emailContent => [
                handleOperatorsEmailAsset(
                    emailContent.id,
                    moduleId,
                    emailContent.operators,
                    apolloClient,
                    emailContentUpdateType,
                    initialValues.emailContents?.find(item => item.id === emailContent.id)?.operators,
                    dealerId
                ),
                handleCustomerEmailContent(
                    emailContent.id,
                    moduleId,
                    emailContent.customers,
                    apolloClient,
                    emailContentUpdateType,
                    initialValues.emailContents?.find(item => item.id === emailContent.id)?.customers,
                    dealerId
                ),
            ]);

            if (promises.length) {
                await Promise.all(promises);
            }
        },
        [apolloClient]
    );
};

export default useHandleMobilityEmailAsset;

type PreparationAssetType = {
    emailContentId: string;
    moduleId: string;
    recipient: MobilityRecipient;
    assetType: MobilityEmailAssetType;
    emailContentUpdateType: EmailContentUpdateType;
    dealerId?: string;
};

const handleMobilityEmailAssetImage = (
    apolloClient: ApolloClient<object>,
    { moduleId, recipient, assetType, emailContentId, emailContentUpdateType, dealerId }: PreparationAssetType,
    mobilityAsset: MobilityAsset,
    introImage: MobilityEmailContentInputForm['introImage'],
    previousImage?: MobilityEmailContentInputForm['introImage']
) => {
    if (introImage instanceof File) {
        return apolloClient.mutate<
            UploadMobilityModuleEmailAssetMutation,
            UploadMobilityModuleEmailAssetMutationVariables
        >({
            mutation: UploadMobilityModuleEmailAssetDocument,
            variables: {
                emailContentId,
                moduleId,
                upload: introImage,
                mobilityAsset,
                mobilityRecipient: recipient,
                assetType,
                emailContentUpdateType,
                dealerId,
            },
        });
    }

    if (isNil(introImage) && isObject(previousImage)) {
        return apolloClient.mutate<
            DeleteMobilityModuleEmailAssetMutation,
            DeleteMobilityModuleEmailAssetMutationVariables
        >({
            mutation: DeleteMobilityModuleEmailAssetDocument,
            variables: {
                emailContentId,
                moduleId,
                mobilityAsset,
                mobilityRecipient: recipient,
                assetType,
                emailContentUpdateType,
                dealerId,
            },
        });
    }

    return Promise.resolve(null);
};

// Handler for operator email assets
const handleOperatorsEmailAsset = async (
    emailContentId: string,
    moduleId: string,
    operators: MobilityOperatorEmailContentInput,
    apolloClient: ApolloClient<object>,
    emailContentUpdateType: EmailContentUpdateType,
    storedValue?: MobilityOperatorEmailContentInput,
    dealerId?: string
) => {
    const uploads = [];

    const preparation: PreparationAssetType = {
        moduleId,
        recipient: MobilityRecipient.Operators,
        assetType: MobilityEmailAssetType.IntroImage,
        emailContentId,
        emailContentUpdateType,
        dealerId,
    };

    // Booking Amendment
    if (operators.bookingAmendment) {
        uploads.push(
            handleMobilityEmailAssetImage(
                apolloClient,
                preparation,
                MobilityAsset.BookingAmendment,
                operators.bookingAmendment.introImage,
                storedValue?.bookingAmendment?.introImage
            )
        );
    }

    // Booking Cancellation
    if (operators.bookingCancellation) {
        uploads.push(
            handleMobilityEmailAssetImage(
                apolloClient,
                preparation,
                MobilityAsset.BookingCancellation,
                operators.bookingCancellation.introImage,
                storedValue?.bookingCancellation?.introImage
            )
        );
    }

    // Booking Confirmation
    if (operators.bookingConfirmation) {
        uploads.push(
            handleMobilityEmailAssetImage(
                apolloClient,
                preparation,
                MobilityAsset.BookingConfirmation,
                operators.bookingConfirmation.introImage,
                storedValue?.bookingConfirmation?.introImage
            )
        );
    }

    if (uploads.length) {
        await Promise.all(uploads);
    }
};

// Handler for customer email assets
const handleCustomerEmailContent = async (
    emailContentId: string,
    moduleId: string,
    customers: MobilityCustomerEmailContentInput,
    apolloClient: ApolloClient<object>,
    emailContentUpdateType: EmailContentUpdateType,
    storedValue?: MobilityCustomerEmailContentInput,
    dealerId?: string
) => {
    const uploads = [];

    const preparation: PreparationAssetType = {
        assetType: MobilityEmailAssetType.IntroImage,
        moduleId,
        recipient: MobilityRecipient.Customers,
        emailContentId,
        emailContentUpdateType,
        dealerId,
    };

    // Booking Amendment
    if (customers.bookingAmendment) {
        uploads.push(
            handleMobilityEmailAssetImage(
                apolloClient,
                preparation,
                MobilityAsset.BookingAmendment,
                customers.bookingAmendment.introImage,
                storedValue?.bookingAmendment?.introImage
            )
        );
    }

    // Booking Cancellation
    if (customers.bookingCancellation) {
        uploads.push(
            handleMobilityEmailAssetImage(
                apolloClient,
                preparation,
                MobilityAsset.BookingCancellation,
                customers.bookingCancellation.introImage,
                storedValue?.bookingCancellation?.introImage
            )
        );
    }

    // Booking CheckedIn
    if (customers.bookingCheckedIn) {
        uploads.push(
            handleMobilityEmailAssetImage(
                apolloClient,
                preparation,
                MobilityAsset.BookingCheckedIn,
                customers.bookingCheckedIn.introImage,
                storedValue?.bookingCheckedIn?.introImage
            )
        );
    }

    // Booking Complete
    if (customers.bookingComplete) {
        uploads.push(
            handleMobilityEmailAssetImage(
                apolloClient,
                preparation,
                MobilityAsset.BookingComplete,
                customers.bookingComplete.introImage,
                storedValue?.bookingComplete?.introImage
            )
        );
    }

    // Booking Confirmation
    if (customers.bookingConfirmation) {
        uploads.push(
            handleMobilityEmailAssetImage(
                apolloClient,
                preparation,
                MobilityAsset.BookingConfirmation,
                customers.bookingConfirmation.introImage,
                storedValue?.bookingConfirmation?.introImage
            )
        );
    }

    // Booking Reminder
    if (customers.bookingReminder) {
        uploads.push(
            handleMobilityEmailAssetImage(
                apolloClient,
                preparation,
                MobilityAsset.BookingReminder,
                customers.bookingReminder.introImage,
                storedValue?.bookingReminder?.introImage
            )
        );
    }

    if (uploads.length) {
        await Promise.all(uploads);
    }
};
